import {useEffect, useState} from "react";
import {OrgSettings} from "../../models/org/orgSettings";
import {LangKey, useGlobalContext, useUserContext} from "../globalContext";
import {addNotificationWithButton} from "../../utils/toast/toast";
import {I18nString} from "../../models/lang/i18nString";

// A cleared language means "no translation", so drop it instead of storing an empty
// string: a present-but-blank value reads as set everywhere it is checked by presence.
export function withoutEmptyValues(ml: I18nString | null | undefined): I18nString | null {
    if (!ml) return null
    const kept: I18nString = {}
    Object.entries(ml).forEach(([lang, v]) => {
        if (v.trim() !== "") kept[lang] = v
    })
    return Object.keys(kept).length > 0 ? kept : null
}

// Mirrors how publishing resolves the address: the plain one backs every language, with
// stored per-language values layered over it. Seeding from the stored map alone would
// leave the primary input blank for records whose translations skip it, even though
// customers are served the plain address there.
export function seedAddressMl(address: string, ml: I18nString | null | undefined, primary: LangKey): I18nString | null {
    const stored = withoutEmptyValues(ml)
    if (!address) return stored
    return {[primary]: address, ...(stored || {})}
}

// Decides what the plain `address` becomes after a multi-language edit. It mirrors the
// primary language only when that language is part of the edited value: editing a
// secondary one must not blank the plain address on records carrying no primary
// translation, since that address is exactly what backs them at publish time. The key is
// present whenever the primary input has been touched, so clearing it still mirrors
// through as empty and reaches validation.
export function mirroredAddress(edited: I18nString, primary: LangKey, current: string): string {
    return primary in edited ? (edited[primary] || "").trim() : current
}

export function useOrgSettings() {
    const {api, i18n} = useGlobalContext()
    const {user, currentMenuId, setOrgInfo, menu} = useUserContext()
    const [selectedLogoImage, setSelectedLogoImage] = useState<File | null>(null);
    const [modal, setModal] = useState<boolean>(false);
    const [mapModal, setMapModal] = useState(false);
    const [lastSubdomain, setLastSubdomain] = useState<string>("");

    const [org, setOrg] = useState<OrgSettings>({
        name: "",
        address: "",
        logoImg: null,
        subdomain: "",
        defaultLang: "RU",
        customDomain: null,
        lat: null,
        lon: null,
        addressMl: null,
    })
    useEffect(() => {
        if (user?.org) {
            setOrg({
                name: user?.org.name,
                address: user?.org.address,
                logoImg: user?.org.logoImg,
                subdomain: user?.org.subdomain,
                defaultLang: user?.org.defaultLang,
                customDomain: user?.org.customDomain,
                lon: user?.org.lon || null,
                lat: user?.org.lat || null,
                addressMl: seedAddressMl(user.org.address, user?.org.addressMl, menu.i18n.defaultLang),
            })
            setLastSubdomain(user?.org.subdomain)
        }
    }, [user?.org])

    const [uploadLoading, setUploadLoading] = useState<boolean>(false);
    const [saveLoading, setSaveLoading] = useState<boolean>(false);

    // The language of the primary address input, i.e. the one TextI18n edits first.
    const addressLang = menu.i18n.defaultLang

    function handleChangeName(s: string) {
        setOrg({...org, name: s})
    }

    // The address is edited as a multi-language value, but `address` is still the field
    // the backend requires and other write paths use, so it mirrors the primary language.
    // Without this the plain `address` keeps whatever was loaded and is re-sent unchanged,
    // ignoring every edit made in the form.
    function handleChangeMlAddress(s: I18nString) {
        setOrg({...org, addressMl: s, address: mirroredAddress(s, addressLang, org.address)})
    }

    function handleDeleteLogoImg() {
        setOrg({...org, logoImg: null})
    }

    function handleChangeSubdomain(s: string) {
        setOrg({...org, subdomain: s})
    }

    function handleChangeCustomDomain(s: string | null) {
        if (s?.trim() === "") s = null
        setOrg({...org, customDomain: s})
    }

    function handleChangeDefaultLang(s: LangKey) {
        setOrg({...org, defaultLang: s})
    }

    function subdomainIsChanged() {
        return org.subdomain !== lastSubdomain
    }

    function handleUploadLogoImg(file: File | null) {
        setSelectedLogoImage(file)
        setModal(true)
    }

    // Picking a point on the map geocodes to a single string; write it into the
    // multi-language value too, otherwise the two addresses immediately diverge again.
    function changeCoordinatesAndAddress(coordinates: number[], address: string) {
        setOrg({
            ...org,
            address: address,
            addressMl: {...(org.addressMl || {}), [addressLang]: address},
            lat: coordinates[0],
            lon: coordinates[1]
        })
    }

    function UploadLogoImage(file: File) {
        api.saveLogoImage({setLoading: setUploadLoading}, file).then((res) => {
            if (res.data) {
                setOrg({...org, logoImg: res.data.newFilename})
            }
        })
    }

    function handleSave() {
        const addressMl = withoutEmptyValues(org.addressMl)
        // `address` is the per-language fallback publishing layers translations over. Prefer
        // whatever is already set (the map pin writes it directly) and derive it from the
        // primary language otherwise. Deliberately no cross-language fallback here: `address`
        // backs every language at publish time, so borrowing one would leak it into the others.
        // An org may legitimately have no address at all, in which case both fields go out
        // empty and the menu landing page renders no address block.
        const address = org.address.trim() || (addressMl?.[addressLang] || "").trim()
        const toSave = {...org, address, addressMl}
        const save = () => {
            if (user?.org) {
                api.saveOrgSettings({setLoading: setSaveLoading}, toSave, user?.org.id).then((res) => {
                    if (res.data) {
                        setOrgInfo(toSave.name, toSave.subdomain, toSave.address, toSave.defaultLang, toSave.logoImg, toSave.customDomain, toSave.addressMl, toSave.lat, toSave.lon)
                    }
                })
            }
        }
        if (subdomainIsChanged()) {
            addNotificationWithButton({
                message: i18n.q_send_subdomain(),
                onSubmit: save,
                loading: saveLoading,
                i18n
            })
        } else {
            save()
        }
    }

    return {
        org,
        user,
        modal,
        currentMenuId,
        selectedLogoImage,
        setSelectedLogoImage,
        setModal,
        handleChangeName,
        handleUploadLogoImg,
        handleDeleteLogoImg,
        handleChangeSubdomain,
        handleChangeDefaultLang,
        UploadLogoImage,
        handleSave,
        handleChangeCustomDomain,
        handleChangeMlAddress,
        saveLoading,
        uploadLoading,
        mapModal,
        setMapModal,
        changeCoordinatesAndAddress,
    }
}